import {
  CloseOutlined,
  CopyOutlined,
  EditOutlined,
  ReloadOutlined,
  SaveOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Button, Col, DatePicker, Input, message, Row, Select, Skeleton, Table } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { ContentBox } from "../../components/layout/content";
import { LoginedLayout } from "../../components/layout/loginLayout";
import { HeaderTitle } from "../../components/styled-components";
import { FormatDateYMDHMS } from "../../functions/function";
import { OrderHydroChat } from "../../res/type";
import { GET, PATCH } from "../../services";
import {
  CHECKER_GET_ORDER_HYDRO_CHAT,
  CHECKER_SEARCH_HYDRO_BY_ORDER,
  CHECKER_UPDATE_HYDRO_CHAT,
} from "../../services/path";
import "./style.css";

const HydroChat = () => {
  const [searchParams, setSearchParams] = useState({
    searchTextOrderNo: "",
    searchTextSKU: "",
    searchTextDate: "",
    searchTextName: "",
    searchTextAlterSKU: "",
    searchTextBatchID: "",
    searchTextBrandName: "",
    searchTextPlatform: "",
    searchTextOperationStatus: "",
  });

  const [editingIndex, setEditingIndex] = useState(-1);
  const [inputEdit, setInputEdit] = useState("");
  const [inputData, setInputData] = useState("");
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const [orders, setOrders] = useState<OrderHydroChat[]>([]);
  const [isReloading, setIsreloading] = useState(false);
  const hasLocalStorageDate = localStorage.getItem("date") !== null;
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(
    hasLocalStorageDate ? null : dayjs(),
  );

  const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(hasLocalStorageDate ? null : dayjs());
  const [isloading, setLoading] = useState(false);

  useEffect(() => {
    // Check if specific localStorage items exist
    const date = localStorage.getItem("date");
    const operationStatus = localStorage.getItem("operationStatus");
    const platform = localStorage.getItem("platform");
    const logistic = localStorage.getItem("logistic");
    const brand = localStorage.getItem("brand");
    if (startDate && endDate && !(date || operationStatus || platform || logistic || brand)) {
      fetchData();
    }
  }, [startDate, endDate]);

  useEffect(() => {
    const date = localStorage.getItem("date");
    const operationStatus = localStorage.getItem("operationStatus") || "";
    const platform = localStorage.getItem("platform") || "";
    const logistic = localStorage.getItem("logistic") || "";
    const brand = localStorage.getItem("brand") || "";
    if (date && (operationStatus || platform || logistic || brand)) {
      const startDateValue = dayjs(date);
      const endDateValue = dayjs(date);
      setStartDate(startDateValue);
      setEndDate(endDateValue);
      setSearchParams((prevSearchParams) => ({
        ...prevSearchParams,
        searchTextStatus: operationStatus,
        searchTextPlatform: platform,
        searchTextLogistic: logistic,
        searchTextBrand: brand,
      }));

      fetchData2(startDateValue, endDateValue);
      localStorage.clear();
    }
  }, []);

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleReloadOrder = () => {
    try {
      setIsreloading(true);
      fetchData2(startDate, endDate);
      localStorage.clear();
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsreloading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      if (inputData) {
        const result = await GET(CHECKER_SEARCH_HYDRO_BY_ORDER(inputData));
        setOrders(result);
      } else {
        fetchData();
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchInputChange = (field: string, value: string) => {
    setSearchParams({ ...searchParams, [field]: value });
    setCurrentPage(1);
  };

  const handleEditClick = (record: OrderHydroChat, index: number) => {
    setEditingIndex(index); // Set the editing index to the clicked row index
    setInputEdit(record.alter_sku); // Set input value to current value
  };

  const handleSaveClick = async (record: OrderHydroChat) => {
    const bodyReq = { alter_sku: inputEdit, no: record.no };
    const result = await PATCH(CHECKER_UPDATE_HYDRO_CHAT, bodyReq);
    if (result.message !== "Item updated successfully") {
      message.error("error :", result.message);
    } else {
      message.success("update Alter SKU successfully.");
      record.alter_sku = inputEdit;
    }

    setInputEdit("");
    setEditingIndex(-1); // Reset editing index
  };

  const handleCloseEditHydro = () => {
    setInputEdit("");
    setEditingIndex(-1);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const searchOrder = CHECKER_GET_ORDER_HYDRO_CHAT(startDate, endDate);
      const result = await GET(searchOrder);
      setOrders(result);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchData2 = async (startDate: any, endDate: any) => {
    try {
      setLoading(true);
      const searchOrder = CHECKER_GET_ORDER_HYDRO_CHAT(startDate, endDate);
      const result = await GET(searchOrder);
      setOrders(result);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const searchInput = (
    field: string,
    phd?: string,
    valueLocal?: string | number | readonly string[] | undefined,
  ) => {
    return (
      <Input
        placeholder={phd}
        value={valueLocal}
        onChange={(e) => handleSearchInputChange(field, e.target.value)}
        onClick={(e) => e.stopPropagation()} // Prevent sorting when clicking on the input
        style={{ width: 200 }}
        suffix={<SearchOutlined style={{ color: "gray" }} />}
      />
    );
  };

  const filteredData = orders.filter((item) => {
    const checkMatch = (fieldValue: string, searchValue: string) => {
      if (!searchValue) return true;
      const searchValues = searchValue.split(", ").map((val: string) => val.trim());
      return searchValues.some((val) => {
        if (val.includes("*")) {
          return fieldValue.toLowerCase().includes(val.replace("*", "").toLowerCase());
        } else {
          const value = val.toLowerCase();
          const field = fieldValue.trim().toLowerCase();
          return field === value;
        }
      });
    };

    return (
      checkMatch(item.order_no ?? "", searchParams.searchTextOrderNo) &&
      checkMatch(item.alter_sku ?? "", searchParams.searchTextAlterSKU) &&
      checkMatch(item.item_name ?? "", searchParams.searchTextName) &&
      checkMatch(item.sku ?? "", searchParams.searchTextSKU) &&
      checkMatch(item.batch_id ?? "", searchParams.searchTextBatchID) &&
      checkMatch(item.brand_name ?? "", searchParams.searchTextBrandName) &&
      checkMatch(item.platform ?? "", searchParams.searchTextPlatform) &&
      checkMatch(item.operation_status ?? "", searchParams.searchTextOperationStatus)
    );
  });

  const pageOptions = ["10", "25", "50", "100"];
  const columns = [
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Order No:
          </span>
          {searchInput("searchTextOrderNo", "Search OrderNo", searchParams.searchTextOrderNo)}
        </div>
      ),
      dataIndex: "order_no",
      key: "order_no",
      sorter: (a: OrderHydroChat, b: OrderHydroChat) => {
        const A = a.order_no ?? "";
        const B = b.order_no ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div>
          <span style={{ marginLeft: 8 }}>{text}</span>
          {text && (
            <CopyOutlined
              onClick={() => handleCopy(text)}
              style={{ cursor: "pointer", marginLeft: "10px" }}
            />
          )}
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Batch:
          </span>
          {searchInput("searchTextBatchID", "Search Batch", searchParams.searchTextBatchID)}
        </div>
      ),
      dataIndex: "batch_id",
      key: "batch_id",
      sorter: (a: OrderHydroChat, b: OrderHydroChat) => {
        const A = a.batch_id ?? "";
        const B = b.batch_id ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
          {text && (
            <CopyOutlined
              onClick={() => handleCopy(text)}
              style={{ cursor: "pointer", marginLeft: "10px" }}
            />
          )}
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Brand:
          </span>
          {searchInput("searchTextBrandName", "Search Brand", searchParams.searchTextBrandName)}
        </div>
      ),
      dataIndex: "brand_name",
      key: "brand_name",
      sorter: (a: OrderHydroChat, b: OrderHydroChat) => {
        const A = a.brand_name ?? "";
        const B = b.brand_name ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
          {text && (
            <CopyOutlined
              onClick={() => handleCopy(text)}
              style={{ cursor: "pointer", marginLeft: "10px" }}
            />
          )}
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Platform:
          </span>
          {searchInput("searchTextPlatform", "Search Platform", searchParams.searchTextPlatform)}
        </div>
      ),
      dataIndex: "platform",
      key: "platform",
      sorter: (a: OrderHydroChat, b: OrderHydroChat) => {
        const A = a.platform ?? "";
        const B = b.platform ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            SKU:
          </span>
          {searchInput("searchTextSKU", "Search Invoice No.", searchParams.searchTextSKU)}
        </div>
      ),
      dataIndex: "sku",
      key: "sku",
      sorter: (a: OrderHydroChat, b: OrderHydroChat) => {
        const A = a.sku ?? "";
        const B = b.sku ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
          {text && (
            <CopyOutlined
              onClick={() => handleCopy(text)}
              style={{ cursor: "pointer", marginLeft: "10px" }}
            />
          )}
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Item Name:
          </span>
          {searchInput("searchTextName", "Search Item Name.", searchParams.searchTextName)}
        </div>
      ),
      dataIndex: "item_name",
      key: "item_name",
      sorter: (a: OrderHydroChat, b: OrderHydroChat) => {
        const A = a.item_name ?? "";
        const B = b.item_name ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "start" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Alter SKU:
          </span>
          {searchInput("searchTextAlterSKU", "Search Invoice No.", searchParams.searchTextAlterSKU)}
        </div>
      ),
      dataIndex: "alter_sku",
      key: "alter_sku",
      sorter: (a: OrderHydroChat, b: OrderHydroChat) => {
        const A = a.alter_sku ?? "";
        const B = b.alter_sku ?? "";
        return A.localeCompare(B);
      },
      render: (text: string, record: OrderHydroChat, index: number) => (
        <div style={{ textAlign: "center" }}>
          {editingIndex === index ? (
            <div style={{ display: "flex", alignItems: "center", padding: "0px 0px 0px 30px" }}>
              <Input type="text" value={inputEdit} onChange={(e) => setInputEdit(e.target.value)} />
              <SaveOutlined
                onClick={() => handleSaveClick(record)}
                style={{ cursor: "pointer", marginLeft: "10px" }}
              />
              <CloseOutlined
                onClick={() => handleCloseEditHydro()}
                style={{ cursor: "pointer", marginLeft: "10px" }}
              />
            </div>
          ) : (
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ width: "100%", margin: 0, justifyContent: "center" }}> {text}</span>
              <EditOutlined
                onClick={() => handleEditClick(record, index)}
                style={{ cursor: "pointer", marginLeft: "10px" }}
              />
            </div>
          )}
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Status:
          </span>
          {searchInput(
            "searchTextOperationStatus",
            "Search Status",
            searchParams.searchTextOperationStatus,
          )}
        </div>
      ),
      dataIndex: "operation_status",
      key: "operation_status",
      sorter: (a: OrderHydroChat, b: OrderHydroChat) => {
        const A = a.operation_status ?? "";
        const B = b.operation_status ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{text}</span>
        </div>
      ),
    },
    {
      title: (
        <div
          style={{ display: "flex", alignItems: "center", marginLeft: "25px", flexWrap: "wrap" }}
        >
          <span
            style={{
              marginRight: "1%",
              marginBottom: "5%",
              flexBasis: "100%",
              width: "100px",
              maxWidth: "100px",
            }}
          >
            Create Date:
          </span>
          {searchInput("searchTextDate", "Search Date", searchParams.searchTextDate)}
        </div>
      ),
      dataIndex: "create_date",
      key: "create_date",
      sorter: (a: OrderHydroChat, b: OrderHydroChat) => {
        const A = a.create_date ?? "";
        const B = b.create_date ?? "";
        return A.localeCompare(B);
      },
      render: (text: string) => (
        <div style={{ textAlign: "center" }}>
          <span style={{ marginLeft: 8 }}>{FormatDateYMDHMS(text)}</span>
        </div>
      ),
    },
  ];

  return (
    <LoginedLayout>
      <ContentBox>
        <HeaderTitle>
          <h2>Orders Hydro Chat</h2>
        </HeaderTitle>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "20px",
          }}
        >
          <div style={{ flex: 3, marginRight: "8px" }}>
            <span>Date Range:</span>
            <DatePicker.RangePicker
              className="small-date-picker"
              style={{ marginLeft: "8px" }}
              value={startDate && endDate ? [dayjs(startDate), dayjs(endDate)] : null}
              showTime={false}
              onChange={(dates) => {
                if (dates) {
                  setStartDate(dates[0]);
                  setEndDate(dates[1]);
                } else {
                  setStartDate(null);
                  setEndDate(null);
                }
              }}
            />
          </div>
          <div style={{ flex: 2 }}>
            <Input.Search
              placeholder="Search for Order"
              value={inputData}
              onChange={(e) => setInputData(e.target.value)}
              onSearch={handleSearch}
            />
          </div>
        </div>
        <Row
          align="middle"
          justify="space-between"
          gutter={[8, 8]}
          style={{ marginBottom: "20px" }}
        >
          <Col flex={3}>
            <span>Show: </span>
            <Select
              value={pageSize.toString()}
              onChange={(value) => handlePageSizeChange(parseInt(value))}
              style={{
                marginRight: "10px", // Remove any shadow effects
              }}
            >
              {pageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
            <Button
              type="primary"
              onClick={handleReloadOrder}
              icon={isReloading ? null : <ReloadOutlined />}
              loading={isReloading}
              style={{
                backgroundColor: "transparent", // Make background transparent
                borderColor: "transparent", // Remove border
                color: "#767676", // Blue color for the icon
                boxShadow: "none",
                marginRight: "10px", // Remove any shadow effects
              }}
            ></Button>
          </Col>
        </Row>
        {isloading ? (
          <Skeleton active />
        ) : (
          <Table
            columns={columns}
            dataSource={filteredData}
            scroll={{ x: 1500 }} // Adjust the value based on your needs
            pagination={{
              position: ["bottomRight"],
              pageSize: pageSize,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
              onShowSizeChange: (_, size) => handlePageSizeChange(size),
              current: currentPage,
              onChange: (page) => setCurrentPage(page),
              total: filteredData.length,
            }}
          />
        )}
      </ContentBox>
    </LoginedLayout>
  );
};

export default HydroChat;
